import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
  Alert, CircularProgress, TextField, MenuItem, Select, InputAdornment,
  LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Avatar,
} from '@mui/material';
import {
  Refresh, Payment, Receipt, Search, FileDownload,
  Email, Visibility, TrendingUp, Warning, CheckCircle,
  AccountBalance, AccessTime, ErrorOutline,
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;
const pct = (v) => `${parseFloat(v || 0).toFixed(1)}%`;

// ── Colores semáforo antigüedad ──────────────────────────────────────────────
const EDAD_COLOR = (dias) => {
  if (dias <= 30) return { bg: '#e8f5e9', text: '#2e7d32', border: '#4caf50' };
  if (dias <= 60) return { bg: '#fff8e1', text: '#f57f17', border: '#ffc107' };
  if (dias <= 90) return { bg: '#fff3e0', text: '#e65100', border: '#ff9800' };
  return { bg: '#fce4ec', text: '#b71c1c', border: '#f44336' };
};

const PIE_COLORS = ['#4caf50', '#ffc107', '#ff9800', '#f44336'];

// ── Semáforo global ──────────────────────────────────────────────────────────
function SemaforoGlobal({ estado }) {
  const map = {
    SALUDABLE: { color: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircle />, label: 'Cartera saludable' },
    EN_RIESGO: { color: '#f57f17', bg: '#fff8e1', icon: <Warning />,      label: 'Cartera en riesgo' },
    CRITICO:   { color: '#b71c1c', bg: '#fce4ec', icon: <ErrorOutline />, label: 'Cartera crítica' },
  };
  const s = map[estado] || map.SALUDABLE;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: s.bg, border: `1px solid ${s.color}` }}>
      <Box sx={{ color: s.color, display: 'flex' }}>{s.icon}</Box>
      <Typography sx={{ color: s.color, fontWeight: 700, fontSize: 13 }}>{s.label}</Typography>
    </Box>
  );
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function Kpi({ icon, label, value, sub, color = '#1565c0', bg = '#e3f2fd' }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, borderTop: `3px solid ${color}`, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: 'text.secondary' }}>{label}</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color, mt: 0.25 }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</Box>
      </Box>
    </Paper>
  );
}

export default function CuentasPorCobrar() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);
  const [error, setError] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [analisis, setAnalisis] = useState(null);

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroEdad, setFiltroEdad] = useState('todos');

  // Dialog recordatorio
  const [dlgEmail, setDlgEmail] = useState({ open: false, cliente: null, factura: null });
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [mensajeEmail, setMensajeEmail] = useState('');

  useEffect(() => { cargarDatos(); }, []);
  useEffect(() => { if (tab === 1 && !analisis) cargarAnalisis(); }, [tab]);

  const cargarDatos = async () => {
    setLoading(true); setError(null);
    try {
      // 3 llamadas en paralelo en vez de 5: el endpoint facturas-pendientes
      // ya devuelve total/cobrado/saldo/dias calculados en backend.
      const [resRes, cliRes, facRes] = await Promise.all([
        axios.get(`${API}/api/cuentas-cobrar/resumen-cartera`, cfg()).catch(() => ({ data: { data: null } })),
        axios.get(`${API}/api/cuentas-cobrar/clientes-con-saldo`, cfg()).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/api/cuentas-cobrar/facturas-pendientes`, cfg()).catch(() => ({ data: { data: [] } })),
      ]);

      setResumen(resRes.data?.data || resRes.data);
      setClientes(cliRes.data?.data || []);
      setFacturas(facRes.data?.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const cargarAnalisis = async () => {
    setLoadingAnalisis(true);
    try {
      const r = await axios.get(`${API}/api/cuentas-cobrar/analisis`, cfg());
      if (r.data.success) setAnalisis(r.data.data);
    } catch (_) {}
    finally { setLoadingAnalisis(false); }
  };

  // Filtrado
  const facsFiltradas = useMemo(() => {
    return facturas.filter(f => {
      const q = busqueda.toLowerCase();
      const matchQ = !q || (f.cliente?.nombre || f.cliente?.razonSocial || '').toLowerCase().includes(q) || (f.secuencial || '').includes(q) || (f.cliente?.ruc || '').includes(q);
      const matchE = filtroEstado === 'todos' || (filtroEstado === 'parcial' ? f.cobrado > 0 : filtroEstado === 'pendiente' ? f.cobrado === 0 : true);
      const matchA = filtroEdad === 'todos' || (filtroEdad === '0-30' ? f.dias <= 30 : filtroEdad === '31-60' ? f.dias > 30 && f.dias <= 60 : filtroEdad === '61-90' ? f.dias > 60 && f.dias <= 90 : f.dias > 90);
      return matchQ && matchE && matchA;
    });
  }, [facturas, busqueda, filtroEstado, filtroEdad]);

  const totalFiltrado = facsFiltradas.reduce((s,f) => s + f.saldo, 0);

  // Exportar CSV
  const exportarCSV = () => {
    const rows = [['Fecha','Cliente','RUC','Documento','Total','Cobrado','Saldo','Días','Estado']];
    facsFiltradas.forEach(f => {
      const estado = f.cobrado > 0 ? 'Parcial' : 'Pendiente';
      rows.push([f.fechaEmision || '—', f.cliente?.nombre || '—', f.cliente?.ruc || '—', f.secuencial || f.numero_recibo || '—', f.total.toFixed(2), f.cobrado.toFixed(2), f.saldo.toFixed(2), f.dias, estado]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `cuentas_cobrar_${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  // Exportar PDF visual
  const exportarPDF = () => {
    const hoy = new Date().toLocaleDateString('es-EC', { day:'2-digit', month:'long', year:'numeric' });
    const totalEmitido  = resumen?.total_facturado || 0;
    const totalCobrado  = resumen?.total_cobrado || 0;
    const saldoPend     = resumen?.saldo_pendiente || 0;
    const tasaCob       = totalEmitido > 0 ? ((totalCobrado / totalEmitido) * 100).toFixed(1) : '0.0';

    // Antigüedad
    const antiguedadData = [
      { label: '0-30 días',  valor: resumen?.antiguedad_saldos?.['0-30']  || 0, color: '#4caf50' },
      { label: '31-60 días', valor: resumen?.antiguedad_saldos?.['31-60'] || 0, color: '#ffc107' },
      { label: '61-90 días', valor: resumen?.antiguedad_saldos?.['61-90'] || 0, color: '#ff9800' },
      { label: '+90 días',   valor: resumen?.antiguedad_saldos?.['90+']   || 0, color: '#f44336' },
    ];
    const maxAnt = Math.max(...antiguedadData.map(d => d.valor), 1);

    // SVG barras antigüedad (600×160)
    const svgBars = antiguedadData.map((d, i) => {
      const barW = 90;
      const barH = Math.round((d.valor / maxAnt) * 120);
      const x = 20 + i * 140;
      const y = 130 - barH;
      return `
        <rect x="${x}" y="${y}" width="${barW}" height="${barH}" fill="${d.color}" rx="4"/>
        <text x="${x + barW/2}" y="${y - 5}" text-anchor="middle" font-size="11" fill="#333">$${d.valor.toFixed(0)}</text>
        <text x="${x + barW/2}" y="148" text-anchor="middle" font-size="10" fill="#666">${d.label}</text>`;
    }).join('');

    // Filas tabla
    const filas = facsFiltradas.map((f, i) => {
      const edadColor = f.dias <= 30 ? '#e8f5e9' : f.dias <= 60 ? '#fff8e1' : f.dias <= 90 ? '#fff3e0' : '#fce4ec';
      const edadText  = f.dias <= 30 ? '#2e7d32' : f.dias <= 60 ? '#f57f17' : f.dias <= 90 ? '#e65100' : '#b71c1c';
      const estado    = f.cobrado > 0 ? 'Parcial' : 'Pendiente';
      const estadoC   = f.cobrado > 0 ? '#fff3e0' : '#e3f2fd';
      const progreso  = f.total > 0 ? Math.round((f.cobrado / f.total) * 100) : 0;
      return `<tr style="background:${i%2===0?'#fff':'#fafafa'}">
        <td>${f.fechaEmision || '—'}</td>
        <td style="font-weight:600">${(f.cliente?.nombre || f.cliente?.razonSocial || '—').substring(0,28)}</td>
        <td style="font-family:monospace;font-size:10px">${f.cliente?.ruc || '—'}</td>
        <td style="font-family:monospace;font-size:10px">${String(f.secuencial||f.numero_recibo||'').padStart(9,'0')}</td>
        <td style="text-align:right">$${f.total.toFixed(2)}</td>
        <td style="text-align:right;color:#2e7d32">$${f.cobrado.toFixed(2)}</td>
        <td style="text-align:right;font-weight:700;color:#d32f2f">$${f.saldo.toFixed(2)}</td>
        <td style="text-align:center">
          <div style="background:#e0e0e0;border-radius:4px;height:6px;width:60px;display:inline-block">
            <div style="background:${progreso===100?'#4caf50':progreso>0?'#ffc107':'#f44336'};height:6px;border-radius:4px;width:${progreso}%"></div>
          </div>
          <span style="font-size:9px;margin-left:3px">${progreso}%</span>
        </td>
        <td style="text-align:center">
          <span style="background:${edadColor};color:${edadText};padding:2px 7px;border-radius:10px;font-size:10px;font-weight:700">${f.dias}d</span>
        </td>
        <td style="text-align:center">
          <span style="background:${estadoC};padding:2px 8px;border-radius:10px;font-size:10px">${estado}</span>
        </td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
<title>Reporte Cuentas por Cobrar</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:12px;color:#1f2937;background:#fff}
  @page{size:A4 landscape;margin:12mm}
  @media print{.no-print{display:none!important}}
  .header{background:linear-gradient(135deg,#1565c0,#0d47a1);color:#fff;padding:18px 24px;border-radius:8px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:center}
  .header h1{font-size:20px;font-weight:700}
  .header .meta{font-size:11px;opacity:.85;text-align:right}
  .kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:16px}
  .kpi{background:#fff;border:1px solid #e0e0e0;border-radius:8px;padding:12px;border-top:3px solid var(--c)}
  .kpi .lbl{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:#6b7280;margin-bottom:4px}
  .kpi .val{font-size:20px;font-weight:700;color:var(--c)}
  .kpi .sub{font-size:10px;color:#9ca3af;margin-top:2px}
  .section{margin-bottom:16px}
  .section-title{font-size:12px;font-weight:700;margin-bottom:8px;display:flex;align-items:center;gap:6px;color:#1f2937}
  .charts{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px}
  .chart-box{background:#fff;border:1px solid #e0e0e0;border-radius:8px;padding:12px}
  .chart-box h3{font-size:11px;font-weight:700;margin-bottom:8px;color:#374151}
  table{width:100%;border-collapse:collapse;font-size:10px}
  th{background:#f5f7fa;padding:6px 8px;text-align:left;font-weight:700;font-size:9px;text-transform:uppercase;letter-spacing:.3px;border-bottom:2px solid #e0e0e0}
  td{padding:5px 8px;border-bottom:1px solid #f0f0f0;vertical-align:middle}
  .footer{margin-top:12px;padding-top:8px;border-top:1px solid #e0e0e0;display:flex;justify-content:space-between;font-size:10px;color:#9ca3af}
  .total-row{background:#e3f2fd!important;font-weight:700}
  .total-row td{border-top:2px solid #1565c0;padding:7px 8px}
</style></head><body>

<div class="header">
  <div>
    <div style="font-size:11px;opacity:.8;margin-bottom:2px">REPORTE FINANCIERO</div>
    <h1>📋 Cuentas por Cobrar</h1>
    <div style="font-size:11px;margin-top:4px;opacity:.85">${facsFiltradas.length} documentos · Generado: ${hoy}</div>
  </div>
  <div class="meta">
    <div style="font-size:15px;font-weight:700">TOTAL PENDIENTE</div>
    <div style="font-size:24px;font-weight:700">$${totalFiltrado.toFixed(2)}</div>
    <div>Tasa de cobranza: ${tasaCob}%</div>
  </div>
</div>

<div class="kpis">
  <div class="kpi" style="--c:#1565c0"><div class="lbl">Total Facturado</div><div class="val">$${fmt(totalEmitido)}</div><div class="sub">${resumen?.cantidad_facturas||0} facturas</div></div>
  <div class="kpi" style="--c:#2e7d32"><div class="lbl">Total Cobrado</div><div class="val">$${fmt(totalCobrado)}</div><div class="sub">${resumen?.facturas_pagadas||0} pagadas</div></div>
  <div class="kpi" style="--c:#f57f17"><div class="lbl">Saldo Pendiente</div><div class="val">$${fmt(saldoPend)}</div><div class="sub">${resumen?.facturas_pendientes||0} pendientes</div></div>
  <div class="kpi" style="--c:#b71c1c"><div class="lbl">Vencido +90 días</div><div class="val">$${fmt(resumen?.antiguedad_saldos?.['90+']||0)}</div><div class="sub">Alto riesgo</div></div>
  <div class="kpi" style="--c:#6a1b9a"><div class="lbl">Tasa Cobranza</div><div class="val">${tasaCob}%</div><div class="sub">del total facturado</div></div>
</div>

<div class="charts">
  <div class="chart-box">
    <h3>⏳ Distribución por Antigüedad del Saldo</h3>
    <svg width="100%" viewBox="0 0 600 165" xmlns="http://www.w3.org/2000/svg">
      ${svgBars}
      <line x1="10" y1="130" x2="590" y2="130" stroke="#e0e0e0" stroke-width="1"/>
    </svg>
  </div>
  <div class="chart-box">
    <h3>📊 Resumen de Cartera</h3>
    ${antiguedadData.map(d => `
    <div style="margin-bottom:10px">
      <div style="display:flex;justify-content:space-between;margin-bottom:3px">
        <span style="font-size:11px">${d.label}</span>
        <span style="font-size:11px;font-weight:700">$${d.valor.toFixed(2)}</span>
      </div>
      <div style="background:#e0e0e0;border-radius:4px;height:8px">
        <div style="background:${d.color};height:8px;border-radius:4px;width:${saldoPend>0?Math.round((d.valor/saldoPend)*100):0}%"></div>
      </div>
    </div>`).join('')}
    <div style="margin-top:12px;padding-top:10px;border-top:1px solid #f0f0f0">
      <div style="display:flex;justify-content:space-between;font-size:11px">
        <span>Documentos filtrados:</span><strong>${facsFiltradas.length}</strong>
      </div>
      <div style="display:flex;justify-content:space-between;font-size:11px;margin-top:4px">
        <span>Clientes únicos:</span>
        <strong>${new Set(facsFiltradas.map(f=>f.cliente?.ruc||f.cliente?.id)).size}</strong>
      </div>
      <div style="display:flex;justify-content:space-between;font-size:11px;margin-top:4px">
        <span>Parcialmente cobradas:</span>
        <strong>${facsFiltradas.filter(f=>f.cobrado>0).length}</strong>
      </div>
    </div>
  </div>
</div>

<div class="section">
  <div class="section-title">🧾 Detalle de Documentos Pendientes de Cobro</div>
  <table>
    <thead>
      <tr>
        <th>Fecha</th><th>Cliente</th><th>RUC / Cédula</th><th>No. Documento</th>
        <th style="text-align:right">Total</th><th style="text-align:right">Cobrado</th>
        <th style="text-align:right">Saldo</th><th style="text-align:center">Cobro %</th>
        <th style="text-align:center">Antigüedad</th><th style="text-align:center">Estado</th>
      </tr>
    </thead>
    <tbody>
      ${filas}
      <tr class="total-row">
        <td colspan="4"><strong>TOTALES (${facsFiltradas.length} documentos)</strong></td>
        <td style="text-align:right"><strong>$${facsFiltradas.reduce((s,f)=>s+f.total,0).toFixed(2)}</strong></td>
        <td style="text-align:right;color:#2e7d32"><strong>$${facsFiltradas.reduce((s,f)=>s+f.cobrado,0).toFixed(2)}</strong></td>
        <td style="text-align:right;color:#d32f2f"><strong>$${totalFiltrado.toFixed(2)}</strong></td>
        <td colspan="3"></td>
      </tr>
    </tbody>
  </table>
</div>

<div class="footer">
  <span>Ai-leth · CRM Contable con IA · ai-leth.com</span>
  <span>Reporte generado el ${hoy} · Los valores están en USD</span>
</div>

<script>window.onload=()=>{window.print();}</script>
</body></html>`;

    const w = window.open('', '_blank', 'width=1200,height=800');
    w.document.write(html);
    w.document.close();
  };

  // Enviar recordatorio
  const enviarRecordatorio = async () => {
    if (!dlgEmail.cliente || !dlgEmail.factura) return;
    setEnviandoEmail(true);
    try {
      await axios.post(`${API}/api/email/enviar-prueba`, {
        destinatario: dlgEmail.cliente?.email || '',
        asunto: `Recordatorio de cobro - Factura ${dlgEmail.factura.secuencial || dlgEmail.factura.numero_recibo}`,
        mensaje: mensajeEmail || `Estimado/a ${dlgEmail.cliente?.nombre || 'cliente'}, le recordamos que tiene una factura pendiente de pago por ${fmt(dlgEmail.factura.saldo)}. Por favor proceda al pago a la brevedad posible.`,
      }, cfg());
      setDlgEmail({ open: false, cliente: null, factura: null });
      setMensajeEmail('');
    } catch (_) {}
    finally { setEnviandoEmail(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>

      {/* ── HEADER ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Cuentas por Cobrar</Typography>
          <Typography variant="body2" color="text.secondary">Gestión de cartera y seguimiento de cobros</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={cargarDatos} size="small">Actualizar</Button>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={exportarCSV} size="small">CSV</Button>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={exportarPDF} size="small" sx={{ color: '#d32f2f', borderColor: '#d32f2f', '&:hover': { bgcolor: '#fce4ec', borderColor: '#b71c1c' } }}>PDF</Button>
          <Button variant="contained" startIcon={<Payment />} onClick={() => navigate('/cuentas-cobrar/registrar-cobro')}
            sx={{ bgcolor: '#1565c0' }}>Registrar Cobro</Button>
          <Button variant="outlined" onClick={() => navigate('/cuentas-cobrar/cobros')}>Ver Cobros</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── KPIs ── */}
      {resumen && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
          <Kpi icon={<AccountBalance />} label="Total Facturado"  value={fmt(resumen.total_facturado)}  sub={`${resumen.cantidad_facturas||0} facturas`}  color="#1565c0" bg="#e3f2fd" />
          <Kpi icon={<CheckCircle />}    label="Total Cobrado"    value={fmt(resumen.total_cobrado)}     sub={`${resumen.facturas_pagadas||0} pagadas`}     color="#2e7d32" bg="#e8f5e9" />
          <Kpi icon={<TrendingUp />}     label="Saldo Pendiente"  value={fmt(resumen.saldo_pendiente)}   sub={`${resumen.facturas_pendientes||0} pendientes`} color="#f57f17" bg="#fff8e1" />
          <Kpi icon={<Warning />}        label="Vencido +90 días" value={fmt(resumen.antiguedad_saldos?.['90+'])} sub="Alto riesgo"  color="#b71c1c" bg="#fce4ec" />
          <Kpi icon={<AccessTime />}     label="% Cobranza"
            value={resumen.total_facturado > 0 ? pct((resumen.total_cobrado / resumen.total_facturado) * 100) : '0%'}
            sub="Tasa de cobro" color="#6a1b9a" bg="#f3e5f5" />
        </div>
      )}

      {/* ── PESTAÑAS ── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label="📋 Cartera" />
        <Tab label="📊 Análisis" />
        <Tab label="👥 Por Cliente" />
      </Tabs>

      {/* ══════════════ TAB 0: CARTERA ══════════════ */}
      {tab === 0 && (
        <>
          {/* Filtros */}
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="Buscar cliente, RUC, documento..." value={busqueda}
              onChange={e => setBusqueda(e.target.value)} sx={{ minWidth: 280 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <Select size="small" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="todos">Todos los estados</MenuItem>
              <MenuItem value="pendiente">Sin cobro</MenuItem>
              <MenuItem value="parcial">Cobro parcial</MenuItem>
            </Select>
            <Select size="small" value={filtroEdad} onChange={e => setFiltroEdad(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="todos">Toda antigüedad</MenuItem>
              <MenuItem value="0-30">0-30 días</MenuItem>
              <MenuItem value="31-60">31-60 días</MenuItem>
              <MenuItem value="61-90">61-90 días</MenuItem>
              <MenuItem value="+90">+90 días</MenuItem>
            </Select>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {facsFiltradas.length} doc. · Pendiente: <strong style={{ color: '#d32f2f' }}>{fmt(totalFiltrado)}</strong>
            </Typography>
          </Paper>

          {/* Tabla */}
          <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#1565c0', color: '#fff', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt sx={{ fontSize: 20 }} />
              <Typography fontWeight={700}>Documentos Pendientes de Cobro</Typography>
              <Chip label={facsFiltradas.length} size="small" sx={{ bgcolor: '#fff', color: '#1565c0', fontWeight: 700, ml: 1 }} />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fa', '& th': { fontWeight: 700, fontSize: 11, py: 1 } }}>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>RUC / Cédula</TableCell>
                    <TableCell>No. Documento</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Cobrado</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>Progreso</TableCell>
                    <TableCell align="center">Antigüedad</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facsFiltradas.length === 0 ? (
                    <TableRow><TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No hay documentos con los filtros seleccionados</Typography>
                    </TableCell></TableRow>
                  ) : (
                    facsFiltradas.map((f, i) => {
                      const ec = EDAD_COLOR(f.dias);
                      const progreso = f.total > 0 ? Math.round((f.cobrado / f.total) * 100) : 0;
                      const clienteRuc = f.cliente?.ruc || f.cliente?.cedula || '';
                      return (
                        <TableRow key={f.id || f.claveAcceso} hover
                          sx={{ bgcolor: i % 2 === 0 ? '#fff' : '#fafafa', '& td': { fontSize: 12, py: 0.8 } }}>
                          <TableCell>{f.fechaEmision || '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.cliente?.nombre || f.cliente?.razonSocial || '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{clienteRuc}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                            {f.tipo === 'RECIBO'
                              ? <Chip label={`RCV ${f.numero_recibo}`} size="small" color="secondary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                              : String(f.secuencial || '').padStart(9, '0')}
                          </TableCell>
                          <TableCell align="right">{fmt(f.total)}</TableCell>
                          <TableCell align="right" sx={{ color: '#2e7d32' }}>{fmt(f.cobrado)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(f.saldo)}</TableCell>
                          <TableCell sx={{ minWidth: 110 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinearProgress variant="determinate" value={progreso}
                                sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: progreso === 100 ? '#4caf50' : progreso > 0 ? '#ffc107' : '#f44336' } }} />
                              <Typography sx={{ fontSize: 10, minWidth: 28, color: 'text.secondary' }}>{progreso}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: 1, bgcolor: ec.bg, border: `1px solid ${ec.border}` }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: ec.text }}>{f.dias}d</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {f.cobrado > 0
                              ? <Chip label="Parcial" color="warning" size="small" sx={{ fontSize: 10, height: 20 }} />
                              : <Chip label="Pendiente" color="info"    size="small" sx={{ fontSize: 10, height: 20 }} />}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Registrar Cobro">
                              <IconButton size="small" color="success"
                                onClick={() => navigate(`/cuentas-cobrar/registrar-cobro?clienteId=${clienteRuc || f.cliente?.id}`)}>
                                <Payment fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar Recordatorio">
                              <IconButton size="small" color="primary"
                                onClick={() => setDlgEmail({ open: true, cliente: f.cliente, factura: f })}>
                                <Email fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Estado de Cuenta">
                              <IconButton size="small"
                                onClick={() => navigate(`/cuentas-cobrar/estado-cuenta/${clienteRuc || f.cliente?.id}`)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {facsFiltradas.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#e3f2fd', borderTop: '2px solid #1565c0' }}>
                <Typography variant="body2" fontWeight={700}>{facsFiltradas.length} documento(s) pendiente(s)</Typography>
                <Typography variant="body2" fontWeight={700} color="error.main">Total: {fmt(totalFiltrado)}</Typography>
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* ══════════════ TAB 1: ANÁLISIS ══════════════ */}
      {tab === 1 && (
        <>
          {loadingAnalisis ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : analisis ? (
            <>
              {/* Semáforo + métricas clave */}
              <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(4,1fr)', gap: 12, marginBottom: 20, alignItems: 'center' }}>
                <SemaforoGlobal estado={analisis.semaforo} />
                <Kpi icon={<TrendingUp />}  label="Tasa morosidad"    value={pct(analisis.tasaMorosidad)}    color="#b71c1c" bg="#fce4ec" />
                <Kpi icon={<CheckCircle />} label="Tasa cobranza"     value={pct(analisis.tasaCobranza)}     color="#2e7d32" bg="#e8f5e9" />
                <Kpi icon={<AccessTime />}  label="Días prom. cobro"  value={`${analisis.diasPromedioCobro}d`} color="#1565c0" bg="#e3f2fd" />
                <Kpi icon={<Warning />}     label="Saldo vencido +90" value={fmt(analisis.saldoVencido)}     color="#e65100" bg="#fff3e0" />
              </div>

              {/* Gráficas: tendencia + antigüedad */}
              <div style={{ display: 'grid', gridTemplateColumns: '60% 38%', gap: 16, marginBottom: 20 }}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700} gutterBottom>📈 Tendencia de Cobros ({new Date().getFullYear()})</Typography>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={analisis.tendencia} margin={{ left: 8, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="mes" fontSize={11} />
                      <YAxis fontSize={11} tickFormatter={v => `$${v}`} width={64} />
                      <RTooltip formatter={(v, n) => [`$${v.toFixed(2)}`, n]} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="emitido"   name="Emitido"   fill="#90caf9" radius={[2,2,0,0]} />
                      <Bar dataKey="cobrado"   name="Cobrado"   fill="#4caf50" radius={[2,2,0,0]} />
                      <Bar dataKey="pendiente" name="Pendiente" fill="#f44336" radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700} gutterBottom>⏳ Distribución por Antigüedad</Typography>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={analisis.distAntiguedad.filter(d => d.valor > 0)} dataKey="valor" nameKey="rango" cx="50%" cy="50%" outerRadius={70} label={({ rango, percent }) => `${rango} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                        {analisis.distAntiguedad.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <RTooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 1 }}>
                    {analisis.distAntiguedad.map((d, i) => (
                      <Box key={d.rango} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PIE_COLORS[i] }} />
                          <Typography variant="caption">{d.rango} días</Typography>
                        </Box>
                        <Typography variant="caption" fontWeight={700}>{fmt(d.valor)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </div>

              {/* Top clientes morosos */}
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={700} gutterBottom>🔴 Top Clientes con Mayor Deuda</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      {['#','Cliente','RUC','Facturas','Saldo Pendiente','Días máx.','Riesgo'].map(h => (
                        <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, py: 0.75 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analisis.topClientes.map((c, i) => {
                      const ec = EDAD_COLOR(c.diasMaximo);
                      return (
                        <TableRow key={c.ruc} hover>
                          <TableCell>
                            <Avatar sx={{ width: 26, height: 26, fontSize: 12, bgcolor: i === 0 ? '#d32f2f' : i < 3 ? '#f57f17' : '#1565c0' }}>
                              {i+1}
                            </Avatar>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{c.nombre}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.ruc}</TableCell>
                          <TableCell align="center">{c.facturas}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(c.saldo)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1, bgcolor: ec.bg, border: `1px solid ${ec.border}` }}>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: ec.text }}>{c.diasMaximo}d</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {c.diasMaximo > 90 ? <Chip label="Crítico"  color="error"   size="small" sx={{ fontSize: 10, height: 20 }} />
                            : c.diasMaximo > 60 ? <Chip label="Alto"     color="warning" size="small" sx={{ fontSize: 10, height: 20 }} />
                            : c.diasMaximo > 30 ? <Chip label="Medio"    color="info"    size="small" sx={{ fontSize: 10, height: 20 }} />
                                                : <Chip label="Bajo"     color="success" size="small" sx={{ fontSize: 10, height: 20 }} />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">No hay datos de análisis disponibles</Typography>
            </Box>
          )}
        </>
      )}

      {/* ══════════════ TAB 2: POR CLIENTE ══════════════ */}
      {tab === 2 && (
        <Paper sx={{ borderRadius: 2 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#1565c0', color: '#fff', borderRadius: '8px 8px 0 0' }}>
            <Typography fontWeight={700}>Clientes con Saldo Pendiente</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fa', '& th': { fontWeight: 700, fontSize: 11, py: 1 } }}>
                <TableCell>Cliente</TableCell>
                <TableCell>Identificación</TableCell>
                <TableCell align="center">Facturas Pend.</TableCell>
                <TableCell align="right">Saldo Total</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Indicador</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientes.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No hay clientes con saldo pendiente</Typography>
                </TableCell></TableRow>
              ) : (
                clientes.map(c => {
                  const maxSaldo = Math.max(...clientes.map(x => parseFloat(x.saldo_total || 0)));
                  const pct2 = maxSaldo > 0 ? (parseFloat(c.saldo_total || 0) / maxSaldo) * 100 : 0;
                  return (
                    <TableRow key={c.cliente_id} hover sx={{ '& td': { fontSize: 12, py: 0.9 } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{c.cliente_nombre}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.cliente_identificacion}</TableCell>
                      <TableCell align="center">
                        <Chip label={c.facturas_pendientes} size="small" color="warning" sx={{ fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(c.saldo_total)}</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <LinearProgress variant="determinate" value={pct2}
                          sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: pct2 > 75 ? '#f44336' : pct2 > 40 ? '#ff9800' : '#4caf50' } }} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Estado de Cuenta">
                          <IconButton size="small" color="primary"
                            onClick={() => navigate(`/cuentas-cobrar/estado-cuenta/${c.cliente_id}`)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Registrar Cobro">
                          <IconButton size="small" color="success"
                            onClick={() => navigate(`/cuentas-cobrar/registrar-cobro?clienteId=${c.cliente_id}`)}>
                            <Payment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enviar Recordatorio">
                          <IconButton size="small" color="info"
                            onClick={() => setDlgEmail({ open: true, cliente: { nombre: c.cliente_nombre, ruc: c.cliente_identificacion }, factura: { saldo: c.saldo_total } })}>
                            <Email fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ── DIALOG RECORDATORIO ── */}
      <Dialog open={dlgEmail.open} onClose={() => setDlgEmail({ open: false, cliente: null, factura: null })} maxWidth="sm" fullWidth>
        <DialogTitle>📧 Enviar Recordatorio de Cobro</DialogTitle>
        <DialogContent>
          {dlgEmail.cliente && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f7fa', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Cliente:</strong> {dlgEmail.cliente.nombre}</Typography>
              <Typography variant="body2"><strong>Saldo pendiente:</strong> {fmt(dlgEmail.factura?.saldo)}</Typography>
            </Box>
          )}
          <TextField fullWidth multiline rows={4} label="Mensaje personalizado (opcional)"
            value={mensajeEmail} onChange={e => setMensajeEmail(e.target.value)}
            placeholder={`Estimado/a ${dlgEmail.cliente?.nombre || 'cliente'}, le recordamos que tiene un saldo pendiente de ${fmt(dlgEmail.factura?.saldo)}...`} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            * Se enviará al correo registrado del cliente usando el SMTP configurado en tu empresa.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgEmail({ open: false, cliente: null, factura: null })}>Cancelar</Button>
          <Button variant="contained" onClick={enviarRecordatorio} disabled={enviandoEmail} startIcon={<Email />}>
            {enviandoEmail ? 'Enviando...' : 'Enviar Recordatorio'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
